import type { ReactNode } from "react"
import { Link } from "@tanstack/react-router"
import { format, formatDistanceToNow } from "date-fns"

import { IssueStatusIcon } from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { Tag } from "@/components/tag"
import { detailPath } from "@/lib/detail-drawer"
import { getProjectIcon } from "@/lib/projects"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import {
	SidebarGroup,
	SidebarGroupLabel,
} from "@/screens/app/threads/components/right-bar/shared/sidebar-group"

export type TagDetailStats = RouterOutputs["tag"]["detailStats"]

type RelatedItem =
	| { kind: "issue"; item: TagDetailStats["issues"][number] }
	| { kind: "project"; item: TagDetailStats["projects"][number] }

const RELATED_LIMIT = 4

// Issues and projects the tag's threads link to most, in one short list.
export function pickRelatedWork(stats: TagDetailStats): RelatedItem[] {
	const items: RelatedItem[] = [
		...stats.issues.map((item) => ({ kind: "issue" as const, item })),
		...stats.projects.map((item) => ({ kind: "project" as const, item })),
	]
	return items
		.sort((a, b) => b.item.count - a.item.count)
		.slice(0, RELATED_LIMIT)
}

const ROW =
	"-mx-1.5 flex min-h-[30px] items-center gap-2.5 rounded-md px-1.5 text-[13px] hover:bg-gray-100 dark:hover:bg-gray-800"

const PropertyRow = ({
	label,
	children,
}: {
	label: string
	children: ReactNode
}) => (
	<div className="grid min-h-[30px] grid-cols-[6rem_minmax(0,1fr)] items-center px-2 text-[13px]">
		<span className="text-secondary">{label}</span>
		<span className="min-w-0 truncate text-xs tabular-nums text-primary">
			{children}
		</span>
	</div>
)

const Count = ({ value }: { value: number }) => (
	<span className="flex-none tabular-nums text-secondary">{value}</span>
)

interface TagDetailSidebarProps {
	stats: TagDetailStats | undefined
	createdAt: number
	groupName: string | undefined
	groupColor: string | undefined
	onEditGroup?: () => void
}

export const TagDetailSidebar = ({
	stats,
	createdAt,
	groupName,
	groupColor,
	onEditGroup,
}: TagDetailSidebarProps) => {
	const related = stats ? pickRelatedWork(stats) : []

	return (
		<div data-testid="tag-detail-sidebar" className="flex flex-col pb-8">
			<SidebarGroup>
				<SidebarGroupLabel>Properties</SidebarGroupLabel>
				<div className="grid min-h-[30px] grid-cols-[6rem_minmax(0,1fr)] items-center px-2 text-[13px]">
					<span className="text-secondary">Group</span>
					<button
						type="button"
						onClick={onEditGroup}
						disabled={!onEditGroup}
						className="-ml-1.5 flex min-w-0 items-center gap-2 justify-self-start rounded-md px-1.5 py-1 text-xs text-primary hover:bg-gray-100 disabled:hover:bg-transparent dark:hover:bg-gray-800"
					>
						<span
							className="h-2 w-2 flex-none rounded-full"
							style={{ backgroundColor: groupColor }}
						/>
						<span className="truncate">{groupName}</span>
					</button>
				</div>
				<PropertyRow label="Created">
					{format(new Date(createdAt), "MMM d, yyyy")}
				</PropertyRow>
				<PropertyRow label="Threads">{stats?.threadCount ?? ""}</PropertyRow>
				<PropertyRow label="Open">{stats?.openCount ?? ""}</PropertyRow>
				<PropertyRow label="Last thread">
					{stats?.lastThreadAt
						? formatDistanceToNow(new Date(stats.lastThreadAt), {
								addSuffix: true,
							})
						: ""}
				</PropertyRow>
			</SidebarGroup>

			{stats && stats.companies.length > 0 && (
				<SidebarGroup>
					<SidebarGroupLabel className="justify-between">
						<span>Companies</span>
						<span className="tabular-nums">{stats.companyCount}</span>
					</SidebarGroupLabel>
					<div className="flex flex-col px-2">
						{stats.companies.map((company) => (
							<Link
								key={company.id}
								to="/customers/$id"
								params={{ id: company.id }}
								className={ROW}
							>
								<CompanyLogo
									brandfetchData={null}
									fallback={company.name}
									linearLogoUrl={company.logoUrl ?? undefined}
									size="xxs+"
								/>
								<span className="min-w-0 flex-1 truncate">{company.name}</span>
								<Count value={company.count} />
							</Link>
						))}
					</div>
				</SidebarGroup>
			)}

			{related.length > 0 && (
				<SidebarGroup>
					<SidebarGroupLabel>Related</SidebarGroupLabel>
					<div className="flex flex-col px-2">
						{related.map((entry) => {
							if (entry.kind === "issue") {
								const issue = entry.item
								return (
									<Link
										key={`issue-${issue.id}`}
										to={detailPath("issue", issue.id)}
										className={ROW}
									>
										<IssueStatusIcon
											className="h-3.5 w-3.5 flex-none"
											status={issue.status}
											color={issue.statusColor ?? undefined}
										/>
										<span className="min-w-0 flex-1 truncate">
											{issue.title}
										</span>
									</Link>
								)
							}
							const project = entry.item
							const ProjectIcon = getProjectIcon(project.icon)
							return (
								<Link
									key={`project-${project.id}`}
									to={detailPath("project", project.id)}
									className={ROW}
								>
									<ProjectIcon
										className="h-3.5 w-3.5 flex-none"
										style={{ color: project.color ?? undefined }}
									/>
									<span className="min-w-0 flex-1 truncate">
										{project.name}
									</span>
								</Link>
							)
						})}
					</div>
				</SidebarGroup>
			)}

			{stats && stats.alsoTagged.length > 0 && (
				<SidebarGroup>
					<SidebarGroupLabel>Also tagged</SidebarGroupLabel>
					<div className="flex flex-wrap gap-1.5 px-2">
						{stats.alsoTagged.map((tag) => (
							<Tag key={tag.id} tag={tag} groupColor={tag.groupColor} isLink />
						))}
					</div>
				</SidebarGroup>
			)}
		</div>
	)
}
